import * as React from 'react';
import clsx from 'clsx';
import { Typography } from '../typography';
import { Combobox, Options } from '@/components/ui/combobox';
import { ButtonContentFilter } from '@/components/screens/lawyerapp/client-directory/filters';
import { DatePicker } from '../date-picker';
import { useMediaQuery } from 'react-responsive';
import { ModalFilter } from './ModalFilter';
import {
	ComboboxMultiple,
	OptionsMultiple,
} from '@/components/ui/comboboxMultiple';
import { ComboboxMultipleSelection } from '@/components/ui/comboboxMultipleSelection';

interface HeaderFilterTableProps {
	title: string;
	hasFilter: boolean;
	searchTitle?: string;
	classNameTitle?: string;
	filterOptions: Options[];
	classNameOptions?: string;
	isLoading?: boolean;
	classNamePopoverContent?: string;
	classNameCommand?: string;
	classNameCommandList?: string;
	classNameCommandGroup?: string;
	classNameCommandItem?: string;
	classNameClear?: string;
	classNameContainer?: string;
	classNameTrigger?: string;
	dateFrom?: string;
	dateTo?: string;
	disabled?: boolean;
	type?: 'combobox' | 'date' | 'combo-multiple' | 'multiple';
	value?: string;
	values?: string[];
	placeholder?: string;
	buttonContentDefault?: boolean;
	isSearch?: boolean;
	containerRef?: (node: HTMLDivElement | null) => void;
	handleSelectFilters?: (value: string | undefined) => void;
	handleSearch?: (value: string) => void;
	handleSelectMultipleFilter?: (key: string, value: string | undefined) => void;
	handleSelectDateFilter?: (
		value: { from: string; to: string } | undefined
	) => void;
	handleClearAll?: () => void;
}

export const HeaderFilterTable: React.FC<HeaderFilterTableProps> = ({
	title,
	hasFilter,
	searchTitle,
	filterOptions,
	disabled,
	// isLoading,
	type = 'combobox',
	classNamePopoverContent,
	classNameCommand,
	classNameCommandList,
	classNameCommandGroup,
	classNameOptions,
	isLoading,
	classNameCommandItem,
	classNameContainer,
	classNameTrigger,
	classNameTitle,
	classNameClear,
	containerRef,
	dateFrom,
	dateTo,
	placeholder,
	value,
	values,
	buttonContentDefault = false,
	handleClearAll,
	isSearch,
	handleSelectFilters,
	handleSelectMultipleFilter,
	handleSelectDateFilter,
	handleSearch,
}) => {
	const [open, setOpen] = React.useState<boolean>(false);
	const [modalOpen, setModalOpen] = React.useState(false);

	const isDesktopOrLaptop = useMediaQuery({
		query: '(min-width: 1024px)',
	});

	const handleOpen = () => {
		if (isDesktopOrLaptop) {
			return setOpen(!open);
		}
		setModalOpen(!modalOpen);
	};

	return (
		<div className={clsx('flex items-center gap-x-2', classNameContainer)}>
			{!buttonContentDefault && (
				<Typography
					type="regular-12"
					className="text-opacity-black whitespace-nowrap"
				>
					{title}
				</Typography>
			)}
			{type === 'combobox' && (
				<>
					<Combobox
						disabled={disabled}
						isSearch={isSearch}
						buttonContent={
							buttonContentDefault ? undefined : (
								<ButtonContentFilter active={hasFilter} />
							)
						}
						title={searchTitle}
						clearAll={() => handleClearAll && handleClearAll()}
						open={open}
						placeholder={placeholder || ''}
						options={filterOptions}
						value={value || ''}
						setOpen={handleOpen}
						setValue={(data) => {
							handleSelectFilters && handleSelectFilters(data);
						}}
						classNameCommand={classNameCommand}
						classNameCommandList={classNameCommandList}
						classNameCommandGroup={classNameCommandGroup}
						classNameClear={classNameClear}
						classNamePopoverContent={classNamePopoverContent}
						classNameCommandItem={classNameCommandItem}
						classNameTitle={classNameTitle}
						classNameOptions={classNameOptions}
						isLoading={isLoading}
						classNameTrigger={clsx('!w-fit !bg-transparent', classNameTrigger)}
						typographyTypeLabel="regular-14"
						containerRef={containerRef}
						handleSearch={handleSearch}
					/>
					<ModalFilter
						open={modalOpen}
						value={value || ''}
						setOpen={setModalOpen}
						filterOptions={filterOptions}
						containerRef={containerRef}
						clearAll={() => handleClearAll && handleClearAll()}
						setValue={(data) =>
							handleSelectFilters && handleSelectFilters(data)
						}
					/>
				</>
			)}

			{type === 'multiple' && (
				<ComboboxMultipleSelection
					open={open}
					setOpen={setOpen}
					disabled={disabled}
					hasFilter={hasFilter}
					buttonContentDefault={buttonContentDefault}
					values={values}
					labelDropdown="Client Segment"
					searchTitle={searchTitle}
					classNameTitle={classNameTitle}
					classNamePopoverContent={classNamePopoverContent}
					classNameClear={classNameClear}
					filterOptions={filterOptions}
					handleClearAll={handleClearAll}
					handleSelectFilters={handleSelectFilters}
				/>
			)}

			{type === 'combo-multiple' && (
				<ComboboxMultiple
					disabled={disabled}
					buttonContent={
						buttonContentDefault ? undefined : (
							<ButtonContentFilter active={hasFilter} />
						)
					}
					open={open}
					title={searchTitle}
					placeholder="View All"
					options={filterOptions as OptionsMultiple[]}
					value={value || ''}
					isDropdown={false}
					classNameCommandList={'!max-h-full'}
					classNameButton="!p-2"
					classNameArrow={'!w-4 !h-4 !mt-0'}
					classNameTrigger="!p-0"
					classNamePopoverContent="!w-[270px]"
					setOpen={handleOpen}
					setValue={(key, value) => handleSelectMultipleFilter?.(key, value)}
					clearAll={handleClearAll ? () => handleClearAll() : undefined}
				/>
			)}

			{type === 'date' && (
				<DatePicker
					disabled={disabled}
					buttonCustom
					sideOffset={0}
					title={searchTitle}
					dateFrom={dateFrom}
					dateTo={dateTo}
					setValue={(data) =>
						handleSelectDateFilter && handleSelectDateFilter(data)
					}
					clearAll={() => handleClearAll && handleClearAll()}
					buttonContent={<ButtonContentFilter active={hasFilter} />}
					classNameTrigger="!p-0 items-start"
				/>
			)}
		</div>
	);
};
